import type { BaseAIAction } from '@ben/columbia/components/assistant/actions/base';
import { CloseButton } from '@ben/columbia/components/assistant/close';
import Icon, { IconName, IconScale } from '@ben/orion/icon';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { useState } from 'react';
import styled, { css, useTheme } from 'styled-components';

// AssistantPreviewProps
export interface AssistantPreviewProps {
  aiActions?: BaseAIAction[];
  onSelectOption: (option: BaseAIAction) => void;
  resetOptions: () => void;
}

const AssistantPreview = ({
  aiActions,
  onSelectOption,
  resetOptions,
}: AssistantPreviewProps) => {
  const [showClose, setShowClose] = useState(false);
  const theme = useTheme();

  if (!aiActions || aiActions.length === 0) {
    return;
  }

  return (
    <AssistantPreviewContainer
      onMouseEnter={() => setShowClose(true)}
      onMouseLeave={() => setShowClose(false)}
    >
      {showClose && (
        <CloseButton
          onClick={resetOptions}
          onMouseEnter={() => setShowClose(true)}
        >
          <Icon
            name={IconName.Close}
            color={theme.palette.neutral[50]}
            scale={IconScale.S}
          />
        </CloseButton>
      )}
      <Typography variant={TypographyVariant.bodySm2}>
        Do you need help with...
      </Typography>
      <OptionList>
        {aiActions.map((option, index) => (
          <HelperOption
            key={`opt-${index}`}
            onClick={() => onSelectOption(option)}
          >
            <Typography variant={TypographyVariant.bodySm1}>
              {option.label}
            </Typography>
          </HelperOption>
        ))}
      </OptionList>
    </AssistantPreviewContainer>
  );
};

export default AssistantPreview;

const AssistantPreviewContainer = styled.div`
  ${({ theme }) => css`
    position: absolute;
    bottom: 60px;
    right: 0;
    width: 200px;
    padding: 10px;
    background-color: ${theme.palette.neutral['00']};
    border: 1px solid #ccc;
    border-radius: ${theme.borderRadius.sm};
    box-shadow: 0 4px 6px ${theme.palette.black(0.1)};
    transition: opacity 0.3s ease-in-out;
  `}
`;

const OptionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const HelperOption = styled.li`
  ${({ theme }) => css`
    cursor: pointer;
    padding: 10px;
    background-color: ${theme.palette.offWhite};
    color: #000;
    border-radius: ${theme.borderRadius.sm};
    margin: 5px 0;
    transition: background-color 0.3s ease-in-out;
    &:hover {
      background-color: ${theme.palette.neutral[60]};
    }
  `}
`;
